
import React, { useState, useRef, useEffect, useCallback } from 'react'
import PlayersList from './PlayersList'
import LobbyInfo from './LobbyInfo'
import TeamPlayersList from './TeamPlayersList'
import MaintenanceWarning from './MaintenanceWarning'
import { useGame } from '@/app/providers/GameContextProvider';
import { GameStep } from '@/lib/types/common/GameStep';
import { GameMode } from '@/lib/types/common/GameMode';
import { useDeviceInfo } from '@/hooks/useIsMobile';
import { IoPeopleSharp } from 'react-icons/io5';
import { motion } from 'framer-motion';
import { FaAngleDown } from 'react-icons/fa';


const Lobby = () => {
    const { gameState, room } = useGame();
    const { isMobile } = useDeviceInfo();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showTopFade, setShowTopFade] = useState(false);
    const [showBottomFade, setShowBottomFade] = useState(false);

    const updateFadeVisibility = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        
        const { scrollTop, scrollHeight, clientHeight } = el;
        const isScrollable = scrollHeight > clientHeight;
        
        setShowTopFade(isScrollable && scrollTop > 5);
        setShowBottomFade(isScrollable && scrollTop < scrollHeight - clientHeight - 5);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        updateFadeVisibility();
        el.addEventListener('scroll', updateFadeVisibility);
        
        // Also update on resize and content changes
        const resizeObserver = new ResizeObserver(updateFadeVisibility);
        resizeObserver.observe(el);

        return () => {
            el.removeEventListener('scroll', updateFadeVisibility);
            resizeObserver.disconnect();
        };
    }, [updateFadeVisibility, gameState]);

    useEffect(() => {
        setIsModalOpen(false);
    }, [gameState?.gameStep]);

    // Check if current player is in individual lobby mode
    const currentPlayer = gameState?.players?.get(room?.sessionId || "");
    const isInIndividualLobby = currentPlayer?.inLobby && gameState?.gameStep === GameStep.RECAP;

    // Determine if we should show lobby components
    const showPlayersList = gameState?.gameStep === GameStep.LOBBY ||
        gameState?.gameStep === GameStep.GUESSING ||
        isInIndividualLobby;
    const showLobbyInfo = gameState?.gameStep === GameStep.LOBBY || isInIndividualLobby;

    // Team modes use TeamPlayersList (Fight, Coop, or Custom with Fight/Coop baseMode)
    const isTeamMode = gameState?.gameMode === GameMode.FIGHT ||
        gameState?.gameMode === GameMode.COOP ||
        (gameState?.gameMode === GameMode.CUSTOM &&
            (gameState?.gameSettings?.baseMode === GameMode.FIGHT ||
                gameState?.gameSettings?.baseMode === GameMode.COOP));

    return (
        <>
            <MaintenanceWarning showInLobby={showLobbyInfo} />
            {!isMobile ? (
                <div className="absolute pl-1 xl:pl-4 top-[70px] 2xl:top-[100px] z-30 select-none">
                    {/* Top fade indicator */}
                    <div 
                        className={`absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-10 transition-opacity duration-200 ${showTopFade ? 'opacity-100' : 'opacity-0'}`}
                    />
                    
                    <div 
                        ref={scrollRef}
                        className="max-h-[80vh] overflow-y-auto overflow-x-visible pr-[160px] scrollbar-hide flex flex-col gap-4 2xl:gap-10"
                    >
                        {isTeamMode ? (
                            <>
                                {showPlayersList && <TeamPlayersList />}
                                {showLobbyInfo && <LobbyInfo />}
                            </>
                        ) : (
                            <>
                                {showPlayersList && <PlayersList />}
                                {showLobbyInfo && <LobbyInfo />}
                            </>
                        )}
                    </div>
                    
                    {/* Bottom fade indicator */}
                    <div 
                        className={`absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/60 to-transparent pointer-events-none z-10 transition-opacity duration-200 ${showBottomFade ? 'opacity-100' : 'opacity-0'}`}
                    />
                </div>
            ) : (
                <>
                    <div className="absolute bottom-4 left-4 flex z-[40]">
                        {/* Hide Results Button */}
                        {showPlayersList && (
                            <div
                                className="h-[70px] w-[70px] cursor-pointer rounded-[100%] flex items-center justify-center shadow-[0_0_3px_1px_rgba(0,0,0,0.5),inset_0_0_3px_1px_rgba(0,0,0,0.5)] hover:bg-[#6F6F6F] bg-[#575757] border-2 border-x-[#c0a270] border-y-[#e0c290] group"
                                onClick={() => setIsModalOpen(!isModalOpen)}
                            >
                                <IoPeopleSharp className="text-4xl text-[#bea881]" />
                            </div>
                        )}
                    </div>
                    <motion.div
                        className="fixed inset-0 flex items-end justify-center z-[10001]"
                        initial={{ y: "100%" }}
                        animate={{ y: isModalOpen ? "0%" : "100%" }}
                        onClick={() => setIsModalOpen(false)}
                    >
                        <div
                            className="bg-[#2a2a2a] mb-4 rounded-lg max-w-md w-full mx-4 relative border-2 border-x-[#c0a270] border-y-[#e0c290] shadow-[0_0_3px_1px_rgba(0,0,0,0.5),inset_0_0_3px_1px_rgba(0,0,0,0.5)]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-20 flex items-center justify-center rounded-full bg-[#575757] border-2 border-x-[#c0a270] border-y-[#e0c290] shadow-[0_0_3px_1px_rgba(0,0,0,0.5),inset_0_0_3px_1px_rgba(0,0,0,0.5)] text-[#bea881] text-3xl"
                                onClick={() => setIsModalOpen(false)}
                            >
                                <FaAngleDown />
                            </button>
                            <div className="max-h-[70vh] flex flex-col gap-7 overflow-y-auto overflow-x-visible pt-6 pb-2 pr-[44px]">
                                {isTeamMode ? (
                                    <>
                                        {showLobbyInfo && <LobbyInfo />}
                                        {showPlayersList && <TeamPlayersList />}
                                    </>
                                ) : (
                                    <>
                                        {showLobbyInfo && <LobbyInfo />}
                                        {showPlayersList && <PlayersList />}
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </>
    );
}

export default Lobby